import IconComponent from "@mdi/react";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { Error } from "@material-ui/icons";
import { mdiChevronDown, mdiChevronRight, mdiContentCopy } from "@mdi/js";
import { Tabs } from "antd";
import { DEFAULT_MIDSTREAM_SETTINGS, NOT_AVAILABLE } from "constants/app.constants";
import { ThroughputProduct } from "constants/chart.constants";
import { PROJECT_FOCUS_FIELDS, SHAPEFILE_FIELDS } from "constants/settings.constants";
import { groupBy } from "lodash";
import { RootState } from "store/rootReducer";
import styled from "styled-components";

import { useCategories, useProperty, useUserEvents } from "hooks";

import { WellDetailCardData } from "api/data";

import { IGroupByListColumn, Uwi } from "models";
import { EntityKind } from "models/entityKind";

import { getProductLabel } from "components/charts/utils/getProductLabel";
import { getMidstreamValue } from "components/groupBy/hooks/useGroupedProductOptions";
import { IconSpinner } from "components/icons";
import { useUserSettings } from "components/user/hooks";

import { useWellDetailsStore } from "./context/WellDetailsStoreProvider";
import { selectCurrentGroup, selectSetActiveGroup } from "./context/wellDetailsSelectors";
import { useWellDetailsData } from "./hooks";
import {
  HEADER_GROUP,
  SubgroupStatus,
  hasOwnSubgroup,
  isSubgroupRequestGroup
} from "./hooks/useWellDetailsData";

/**
 * What a tab says when its category holds no columns at all. Nothing is
 * fetched for those tabs, so without a message the body is simply blank.
 */
const EMPTY_GROUP_MESSAGES: Record<string, string> = {
  [SHAPEFILE_FIELDS]:
    "No shapefile sampled in this session. Select a shapefile in a focus field to sample it.",
  [PROJECT_FOCUS_FIELDS]: "No project fields. Add fields to the project to see them here."
};
const DEFAULT_EMPTY_GROUP_MESSAGE = "No fields in this group.";

/** Shown under a geo subgroup whose calculation job has never produced values. */
const NO_DATA_AVAILABLE = "No data available.";

/**
 * What a geo tab says when the known-jobs filter dropped every one of its
 * subgroups. Not the empty-group message: the fields are there, and telling the
 * reader the group has none sends them looking where there is nothing to find.
 */
const NO_LISTED_GEO_JOBS =
  "No fields to show. This well's values belong to geo jobs you don't have access to, or that no longer exist.";

/** The tab whose body is a PDF rather than rows of fields. */
const TYPE_LOG_GROUP = "Type Log";

/** Matches the well card panel's own collapse rows. */
const CHEVRON_SIZE = 1;

/**
 * Stands in for a field whose chunk has not landed yet. A group's rows appear
 * as its chunks arrive, so the rows still in flight must not read "n/a" — that
 * is the answer for a field the service has no value for, and it would flip to
 * a real value moments later.
 */
const PENDING_VALUE = "…";

/** Stateless parser — one is enough for the module. */
const uwiParser = new Uwi();

/**
 * A value worth offering a copy button for. "n/a" is an absence, not data.
 * Matched case-insensitively and trimmed, as the data table does for the same
 * sentinel — the service's casing is not something this card should rely on,
 * and an "N/A" that slipped through would be offered for copying.
 */
function isCopyableValue(value: Maybe<string | Blob>): boolean {
  if (typeof value !== "string") return false;
  const normalized = value.trim().toLowerCase();
  return normalized !== "" && normalized !== NOT_AVAILABLE.toLowerCase();
}

interface WellCardInput {
  entityKind: EntityKind;
  /**
   * Pins the card to one well instead of the map selection. Only the well card
   * panel passes it; widget mounts leave it undefined.
   */
  uwi?: Maybe<string>;
  /**
   * Drops the big primary cell at the top of the header grid. The well card
   * panel passes it because its own header already shows the UWI; widget
   * mounts leave it undefined and keep the heading.
   */
  hideEntityName?: boolean;
  /** Right-aligns every value in the header grid, numeric or not. */
  alignHeaderValuesRight?: boolean;
}

export default function WellCard({
  entityKind = EntityKind.Well,
  uwi,
  hideEntityName = false,
  alignHeaderValuesRight = false
}: WellCardInput) {
  // state
  const [tabs, setTabs] = useState([]);
  const [activeKey, setActiveKey] = useState("Header");
  const { sendWellTicketTabChangeEvent } = useUserEvents();
  // custom hooks
  const {
    selectedWell,
    selectedFacility,
    headerProperties,
    facilityHeaderProperties,
    skippedGroups,
    isLoading,
    error,
    typeLogMessage,
    wellDetails,
    loadedGroups,
    fetchingGroups,
    failedGroups,
    subgroupStatus,
    requestSubgroup,
    retrySubgroup,
    geoJobsSettled,
    unreadyGeoJobs,
    knownGeoJobs
  } = useWellDetailsData({ entityKind, uwi });
  // EVA-10: both lists come back from `useCategories()` already filtered by
  // field-group flag — a category (e.g. "Header", "Forecast", "Performance")
  // whose "<Category> Fields" flag is disabled is dropped there so every
  // consumer (the Well/Facility Ticket tab lists here, and
  // PerformanceDataAnalyzer's column builder) sees the same gating from one
  // place. The facility tab effect below must therefore tolerate any
  // category being absent — including "Performance", whose columns it
  // borrows for the Volumes tab.
  const { categories, facilityCategories } = useCategories();
  const currentGroup = useWellDetailsStore(selectCurrentGroup);
  const setActiveGroup = useWellDetailsStore(selectSetActiveGroup);
  const activeEntities = useSelector((state: RootState) => state.app.activeEntityKinds);
  const userSettings = useUserSettings();
  const isMidstreamDataSourceEnabled =
    userSettings?.midstreamSettings?.enabled ?? DEFAULT_MIDSTREAM_SETTINGS;
  const headers =
    entityKind === EntityKind.Well ? headerProperties : facilityHeaderProperties;

  // The band's own group, not the open tab's: it is on screen whatever tab is
  // active, so it reads as pending until the Header group has answered.
  const isHeaderPending =
    !loadedGroups.includes(HEADER_GROUP) || fetchingGroups.includes(HEADER_GROUP);
  // Formatted only when it is really a uwi. A facility id, and a Future
  // Location's stick name, are not: the formatter strips their "-" and "/"
  // and hands back a mangled string, so those stand in as they came.
  const target = selectedWell ?? selectedFacility;
  const formattedTarget = target
    ? uwiParser.isUwi(target)
      ? uwiParser.toFormatted(target)
      : target
    : undefined;

  const isWell = entityKind === EntityKind.Well;
  const isFacility = entityKind === EntityKind.Facility;
  const message = isWell
    ? "Please select a well on the map"
    : "Please select a facility on the map";

  // Effects and callbacks
  useEffect(() => {
    if (!isWell) return;
    if (!categories) return;
    const typeLogTab = {
      name: "Type Log",
      hexColor: "#57B586",
      sortOrder: 101,
      property: "",
      columns: [],
      id: "TypeLog"
    };
    const categoriesWithTypeLog = [...categories, typeLogTab];

    const newTabs = categoriesWithTypeLog.reduce((list, group) => {
      if (!skippedGroups.includes(group.name)) {
        const failedGroup = failedGroups.find((failed) => failed.group === group.name);
        const isGroupFetching = fetchingGroups.includes(group.name);
        // The 401/404 message belongs to the type log and nothing else. It is
        // set for whichever group answered that way, and any other tab reading
        // it as "loaded" renders a full body of "n/a" rows for a group that
        // was never fetched.
        const isTypeLog = group.name === TYPE_LOG_GROUP;
        if (loadedGroups.includes(group.name) || (isTypeLog && typeLogMessage)) {
          const children = [];
          // The band above the tabs already carries these, so the rows below
          // are built without them — and the message has to be decided on the
          // same list, or a category whose every column is a band property
          // renders a blank tab.
          const visibleColumns = group.columns.filter(
            (c) => !headerProperties.includes(c.property)
          );
          if (!isTypeLog && visibleColumns.length === 0) {
            children.push(
              <TabEmptyMessage key={`${group.name}-empty`}>
                {EMPTY_GROUP_MESSAGES[group.name] ?? DEFAULT_EMPTY_GROUP_MESSAGE}
              </TabEmptyMessage>
            );
          }
          const isSubgroupGated = isSubgroupRequestGroup(group.name);
          // Only on a gated tab. A column whose subgroup came back absent or
          // empty has no dropdown to sit under, so those are the tab's own
          // rows: drawn here with no heading and no expander, and asked for
          // with the tab. Grouped with the rest they land under lodash's
          // "undefined" key, which on a gated tab is an expander the user is
          // invited to click, headed "undefined". A tab drawn whole has no
          // expanders to be misled by, and is grouped exactly as it always was.
          const ownRows = isSubgroupGated
            ? visibleColumns.filter((c) => !hasOwnSubgroup(c))
            : [];
          const subgroups = groupBy(
            isSubgroupGated ? visibleColumns.filter(hasOwnSubgroup) : visibleColumns,
            "subgroup"
          );
          if (ownRows.length > 0) {
            children.push(
              <WellDetailsContainer key={`${group.name}-own-rows`}>
                {ownRows.map((column) => (
                  <WellCardDataValue
                    key={column.property}
                    propertyName={column.property}
                    entityKind={entityKind}
                    wellDetails={wellDetails}
                    pending={isGroupFetching}
                  />
                ))}
              </WellDetailsContainer>
            );
          }
          // These headings come from the field catalogue, which keeps a column
          // for every geo job that has ever written one — which is why the tab
          // listed jobs no other surface shows. Matching the job lists every
          // other surface reads drops the ones that have been deleted, and the
          // EVA Research ones a lapsed tier no longer covers.
          //
          // It decides what the tab lists, and nothing more: the service
          // answers for whatever fields it is asked for, so this is not a
          // protection and must not be read as one.
          //
          // Absent only before the lists have answered, and a geo tab is not
          // requested until they have. An empty set is the lists answering that
          // this reader may see none of the jobs (the server omits a lapsed
          // org's EVA Research jobs), and the tab lists none.
          const knownJobs = knownGeoJobs[group.name];
          const listOnlyKnownJobs = knownJobs !== undefined;
          const subgroupNames = Object.keys(subgroups);
          const droppedEverySubgroup =
            subgroupNames.length > 0 &&
            subgroupNames.every((name) => !knownJobs?.has(name));
          for (const [subgroup, subgroupColumns] of Object.entries(subgroups)) {
            const columns = subgroupColumns as IGroupByListColumn[];
            if (listOnlyKnownJobs && !knownJobs.has(subgroup)) continue;
            // In a geo group the subgroup is the calculation job's name, so a
            // job that has never finished is a whole subgroup with nothing
            // behind it. Read from the readiness the request was built with,
            // not from the live job list: a job that finishes while the card
            // is open is not in the fields already asked for, so dropping the
            // message would leave its rows reading "n/a" for ever.
            // Drawn as a dropdown like its neighbours rather than as a bare
            // heading, so an unfinished job is one more subgroup in the same
            // list, whatever it has to say.
            const isUnreadyJob = unreadyGeoJobs[group.name]?.has(subgroup) ?? false;
            // A geo tab carries one subgroup per calculation job in the org and
            // runs to thousands of rows — enough DOM to lock the page up, and
            // rebuilt on every chunk that lands. Its subgroups open one at a
            // time instead, and each one is asked for when it is opened. Every
            // other tab is a small fixed list, drawn open and fetched whole,
            // and stays as it was.
            if (isSubgroupGated) {
              children.push(
                <CollapsibleSubgroup
                  key={`${subgroup}-section`}
                  group={group.name}
                  title={subgroup}
                  columns={columns}
                  entityKind={entityKind}
                  wellDetails={wellDetails}
                  status={subgroupStatus[group.name]?.[subgroup]}
                  unready={isUnreadyJob}
                  geoJobsSettled={geoJobsSettled}
                  onExpand={requestSubgroup}
                  onRetry={retrySubgroup}
                />
              );
              continue;
            }
            children.push(
              <WellDetailsGroupTitle key={subgroup}>{subgroup}</WellDetailsGroupTitle>
            );
            children.push(
              <WellDetailsContainer key={`${subgroup}-container`}>
                {columns.map((column) => {
                  return (
                    <WellCardDataValue
                      key={column.property}
                      propertyName={column.property}
                      entityKind={entityKind}
                      wellDetails={wellDetails}
                      pending={isGroupFetching}
                    />
                  );
                })}
              </WellDetailsContainer>
            );
          }
          // The known-jobs filter dropped every subgroup this tab had, which
          // the empty-group branch above cannot have caught: that one is
          // decided on the column list, and the columns are all still there.
          // Without a message the tab opens blank with nothing to read.
          //
          // Keyed on the filter rather than on `children` being empty, because
          // the message is about geo jobs specifically. A later filter on some
          // other subgroup tab would leave `children` empty for a reason of its
          // own, and would inherit this wording without anyone noticing.
          if (!isTypeLog && listOnlyKnownJobs && droppedEverySubgroup) {
            children.push(
              <TabEmptyMessage key={`${group.name}-no-listed-geo-jobs`}>
                {NO_LISTED_GEO_JOBS}
              </TabEmptyMessage>
            );
          }
          if (isTypeLog) {
            if (!typeLogMessage) {
              // create link to pdf
              const pdfBlob = new Blob([wellDetails?.[0]], {
                type: "application/pdf"
              });
              const pdfObjectUrl = URL.createObjectURL(pdfBlob);
              children.push(
                <object
                  key="type-log-pdf"
                  data={pdfObjectUrl}
                  type="application/pdf"
                  width="100%"
                  height="100%">
                  <p>
                    PDF settings may be configured to by download PDFs on your browser, if
                    so, navigate to the PDF documents settings to open PDFs in browser.
                    You can download the PDF{" "}
                    <a
                      href={pdfObjectUrl}
                      target="_blank"
                      download={`${selectedWell}.pdf`}
                      rel="noopener noreferrer">
                      here
                    </a>
                    .
                  </p>
                </object>
              );
            } else {
              children.push(<div key="type-log-message">{typeLogMessage}</div>);
            }
          }

          list.push({
            label: group.name,
            key: group.name,
            children: (
              <TabContentContainer key={`${group.name}-content`}>
                {children}
              </TabContentContainer>
            )
          });
        } else if (failedGroup) {
          list.push({
            label: group.name,
            key: group.name,
            children: (
              <TabErrorContainer>
                {`Could not load ${group.name}.`}
                <TabRetryButton onClick={failedGroup.retry}>Retry</TabRetryButton>
              </TabErrorContainer>
            )
          });
        } else if (selectedWell) {
          list.push({
            label: group.name,
            key: group.name,
            children: (
              <TabLoadingContentContainer key={`${group.name}-loading`}>
                <StyledSpinner />{" "}
                {`Loading ${group.name} for ${new Uwi().toFormatted(selectedWell)}`}
              </TabLoadingContentContainer>
            )
          });
        }
      }

      return list;
    }, []);
    setTabs(newTabs);
  }, [
    categories,
    isWell,
    loadedGroups,
    fetchingGroups,
    failedGroups,
    subgroupStatus,
    requestSubgroup,
    retrySubgroup,
    geoJobsSettled,
    unreadyGeoJobs,
    knownGeoJobs,
    skippedGroups,
    headerProperties,
    selectedWell,
    typeLogMessage
  ]);

  useEffect(() => {
    if (!isFacility) return;
    if (!facilityCategories) return;
    const gasProducts = ThroughputProduct.find(
      (product) => product.name === "Gas"
    ).items.map(getMidstreamValue);
    const performanceColumns = facilityCategories.find(
      (category) => category.name === "Performance"
    )?.columns;
    const gasProductsColumns = performanceColumns?.filter((column) =>
      gasProducts.includes(column.product)
    );
    const newTabs = facilityCategories.reduce((list, group) => {
      if (!skippedGroups.includes(group.name)) {
        let groupName = group.name;
        if (groupName == "Licensed Volumes") {
          groupName = "Volumes";
        }
        const failedGroup = failedGroups.find((failed) => failed.group === group.name);
        const isGroupFetching = fetchingGroups.includes(group.name);
        if (loadedGroups.includes(group.name)) {
          const isVolumesGroupName = groupName === "Volumes";
          const children = [];
          const subgroups = groupBy(group.columns, "subgroup");
          const performanceSubgroups = [];
          // gasProductsColumns is undefined when the "Performance" category
          // was flag-filtered out of facilityCategories — skip the injected
          // Performance section entirely rather than crash on it.
          if (isVolumesGroupName && gasProductsColumns) {
            gasProducts.forEach((product) => {
              const productName = getProductLabel(product);
              performanceSubgroups[productName] = gasProductsColumns.filter(
                (column) => column.product === product
              );
            });
          }
          // Volumes also draws the borrowed gas columns, so it is only empty
          // when those are gone too.
          if (
            group.columns.length === 0 &&
            !(isVolumesGroupName && gasProductsColumns?.length)
          ) {
            children.push(
              <TabEmptyMessage key={`${group.name}-empty`}>
                {EMPTY_GROUP_MESSAGES[group.name] ?? DEFAULT_EMPTY_GROUP_MESSAGE}
              </TabEmptyMessage>
            );
          }
          for (const [subgroup, subgroupColumns] of Object.entries(subgroups)) {
            const columns = subgroupColumns as IGroupByListColumn[];
            let subGroupName = subgroup;
            if (subGroupName == "General" && isVolumesGroupName) {
              subGroupName = "Licensed Volumes";
            }
            children.push(<WellDetailsGroupTitle>{subGroupName}</WellDetailsGroupTitle>);
            children.push(
              <WellDetailsContainer>
                {columns.map((column) => {
                  return (
                    <WellCardDataValue
                      key={column.property}
                      propertyName={column.property}
                      entityKind={entityKind}
                      wellDetails={wellDetails}
                      pending={isGroupFetching}
                    />
                  );
                })}
              </WellDetailsContainer>
            );
          }
          if (isVolumesGroupName && gasProductsColumns) {
            children.push(<WellDetailsGroupTitle>{"Performance"}</WellDetailsGroupTitle>);
            for (const [productIndex, [product, columns]] of Object.entries(
              performanceSubgroups
            ).entries()) {
              const isLastProduct =
                productIndex === Object.entries(performanceSubgroups).length - 1;
              const element = (
                <>
                  <WellDetailsGroupTitle>{product}</WellDetailsGroupTitle>
                  <PerformanceDetailsContainer
                    className={`${isLastProduct ? "last-item" : ""}`}>
                    {columns.map((column) => (
                      <WellCardDataValue
                        key={column.property}
                        propertyName={column.property}
                        entityKind={entityKind}
                        wellDetails={wellDetails}
                        pending={isGroupFetching}
                      />
                    ))}
                  </PerformanceDetailsContainer>
                </>
              );
              children.push(element);
            }
          }
          list.push({
            label: groupName,
            key: group.name,
            children: <TabContentContainer>{children}</TabContentContainer>
          });
        } else if (failedGroup) {
          list.push({
            label: groupName,
            key: group.name,
            children: (
              <TabErrorContainer>
                {`Could not load ${groupName}.`}
                <TabRetryButton onClick={failedGroup.retry}>Retry</TabRetryButton>
              </TabErrorContainer>
            )
          });
        } else if (selectedFacility) {
          list.push({
            label: groupName,
            key: group.name,
            children: (
              <TabLoadingContentContainer>
                <StyledSpinner />{" "}
                {`Loading ${groupName} for ${new Uwi().toFormatted(selectedFacility)}`}
              </TabLoadingContentContainer>
            )
          });
        }
      }

      return list;
    }, []);

    setTabs(newTabs);
  }, [
    facilityCategories,
    loadedGroups,
    fetchingGroups,
    failedGroups,
    isFacility,
    skippedGroups,
    facilityHeaderProperties,
    selectedFacility
  ]);

  // EVA-10: clamp — `currentGroup` (default "Header") can name a category
  // the flag gate has removed; without a fallback antd renders no active
  // tab and an empty pane. Its own effect (not part of the tab builders
  // above) so a tab click or an external currentGroup write re-evaluates
  // the clamp without rebuilding the whole tab tree; converges because the
  // re-run finds fallbackKey === currentGroup and writes nothing.
  // Guarded on the category list being real: pre-fetch it's `[]` (not
  // nullish), and clamping against the synthetic-Type-Log-only tab list
  // would PERSIST "Type Log" as the active group before the true tabs
  // exist.
  const hasCategories = (isWell ? categories : facilityCategories)?.length > 0;

  useEffect(() => {
    if (!hasCategories || !tabs.length) return;
    const fallbackKey = tabs.find((tab) => tab.key === currentGroup)?.key ?? tabs[0].key;
    if (fallbackKey !== currentGroup) setActiveGroup(fallbackKey);
    setActiveKey(fallbackKey);
  }, [tabs, currentGroup, setActiveGroup, hasCategories]);

  const onTabChange = useCallback(
    (activeKey: string) => {
      sendWellTicketTabChangeEvent({
        tabName: activeKey,
        entityKind,
        selectedItem: isWell ? selectedWell : selectedFacility
      });
      setActiveGroup(activeKey);
      setActiveKey(activeKey);
    },
    [
      setActiveGroup,
      sendWellTicketTabChangeEvent,
      entityKind,
      isWell,
      selectedWell,
      selectedFacility
    ]
  );

  return (
    <WellCardContainer>
      {((isLoading && loadedGroups?.length === 0) ||
        error ||
        (!selectedWell && isWell) ||
        (!selectedFacility && isFacility)) && (
        <OverlayContainer>
          <OverlayMessages>
            {isLoading && loadedGroups?.length === 0 && (
              <div data-testid="loading-spinner">
                <StyledSpinner />
              </div>
            )}
            {error && (
              <ErrorContainer data-testid="error-container">
                <Error style={{ top: 3 }} /> {error}
              </ErrorContainer>
            )}
            {((!selectedWell && isWell) || (!selectedFacility && isFacility)) && (
              <NoSelectedWellContainer data-testid={"no-well-container"}>
                {(() => {
                  if (
                    (activeEntities.includes(EntityKind.Facility) && isFacility) ||
                    (activeEntities.includes(EntityKind.Well) && isWell)
                  ) {
                    return message;
                  }
                  if (isWell && !activeEntities.includes(EntityKind.Well)) {
                    return "Wells are hidden";
                  }
                  if (isFacility) {
                    return isMidstreamDataSourceEnabled
                      ? "Midstream is hidden"
                      : "Midstream is disabled";
                  }
                })()}
              </NoSelectedWellContainer>
            )}
          </OverlayMessages>
        </OverlayContainer>
      )}
      <HeaderDetailsContainer>
        {headers &&
          headers.map((propertyName, i) => {
            if (hideEntityName && i === 0) return null;
            return (
              <WellCardDataValue
                key={i}
                propertyName={propertyName}
                primary={i === 0}
                alignRight={alignHeaderValuesRight}
                entityKind={entityKind}
                wellDetails={wellDetails}
                pending={isHeaderPending}
                // The name is the band's heading, so with none reported it
                // falls back to the uwi rather than reading "n/a" — the same
                // fallback the card panel's header and its preview popup use.
                // The uwi is known from the moment the target is picked, so it
                // stands in while the group is still in flight as well.
                fallback={i === 0 ? formattedTarget : undefined}
              />
            );
          })}
      </HeaderDetailsContainer>
      <TabbedInfoContainer>
        <Tabs activeKey={activeKey} type="card" items={tabs} onChange={onTabChange} />
      </TabbedInfoContainer>
    </WellCardContainer>
  );
}

interface CollapsibleSubgroupProps {
  /** The tab this subgroup belongs to, which is what is asked for with it. */
  group: string;
  /** One of the two geo tabs today, so this is the calculation job's name. */
  title: string;
  columns: IGroupByListColumn[];
  entityKind: EntityKind;
  wellDetails: WellDetailCardData;
  /** How far along this subgroup's own request is; absent means unasked. */
  status?: SubgroupStatus;
  /**
   * This subgroup's calculation job has never produced values, so it is inert:
   * nothing is ever asked for it, and the catalogue's column count is hidden
   * rather than promising values that do not exist.
   */
  unready?: boolean;
  /** The geo job lists have answered, so a geo request is no longer declined. */
  geoJobsSettled: boolean;
  onExpand: (group: string, subgroup: string) => void;
  onRetry: (group: string, subgroup: string) => void;
}

/**
 * One subgroup of a gated tab, closed until asked for. Its rows are mounted
 * only while it is open, which is the whole point: a geo tab carries every job
 * in the org, and rendering all of them at once is thousands of rows of real
 * DOM — rebuilt again on every chunk that lands.
 *
 * Opening it is also what asks the service for its fields. A geo tab used to be
 * fetched whole, so the one job the user wanted waited behind every other one —
 * minutes of it. Each section owns its own open state so a toggle re-renders
 * one subgroup rather than the tab.
 *
 * Written against a subgroup rather than a job because that is all it needs to
 * know; only the two geo tabs are drawn this way, and no other tab should be
 * without the fetching to pay for it.
 */
function CollapsibleSubgroup({
  group,
  title,
  columns,
  entityKind,
  wellDetails,
  status,
  unready = false,
  geoJobsSettled,
  onExpand,
  onRetry
}: CollapsibleSubgroupProps) {
  const [expanded, setExpanded] = useState(false);

  // Asked for whenever it is open and nothing is on its way, rather than once
  // on the click. Two things undo a click: a geo tab declines the request until
  // its job lists have answered, and pointing the card at another well clears
  // the request list under a section the user left open. Either way the click
  // has been and gone, and a section that only asks on the click sits on its
  // spinner for ever. Asking again costs nothing — the request list keeps the
  // first one.
  //
  // `geoJobsSettled` is in the list because it is what the first case waits
  // for; `status` covers the second, going back to undefined when the list is
  // cleared. Neither is here to trigger a re-run by changing identity: this
  // effect must not depend on `onExpand` being rebuilt, or stabilising that
  // callback later would leave an expanded geo dropdown spinning for ever with
  // nothing failing loudly.
  useEffect(() => {
    if (expanded && !unready && status === undefined) onExpand(group, title);
  }, [expanded, unready, status, geoJobsSettled, group, title, onExpand]);

  // Nothing has come back yet, so the rows would all read "n/a" — the answer
  // for a field the service has no value for, not for one still on its way.
  const isWaiting = status === undefined || status === "loading";

  const toggle = () => setExpanded((open) => !open);

  const body = () => {
    // Nothing was ever asked for, so there is nothing to wait on or to retry.
    if (unready) return <SubgroupNotice>{NO_DATA_AVAILABLE}</SubgroupNotice>;
    if (isWaiting) {
      return (
        <SubgroupNotice role="status">
          <StyledSpinner />
          {/* The spinner alone would announce nothing, and the region is read
              out by its text, not by a label on a child of it. */}
          <span className="visually-hidden">{`Loading ${title}`}</span>
        </SubgroupNotice>
      );
    }
    // The same offer the tab makes when its own fetch fails. Left as rows, a
    // failed request reads exactly like a service with no value for any of
    // these fields, and there is nothing the user can do about it.
    // The live region goes on the text and stops there. A region announces
    // what changed, so a control inside one gets read out as part of the
    // announcement with no way to reach it — which is as true of `alert` as of
    // `status`, and `alert` interrupts on top. The button sits beside the
    // region, reachable in tab order like any other. The loading notice above
    // is text alone, so it keeps `status` on the whole thing.
    if (status === "failed") {
      return (
        <SubgroupNotice>
          <span role="status">{`Could not load ${title}.`}</span>
          <TabRetryButton onClick={() => onRetry(group, title)}>Retry</TabRetryButton>
        </SubgroupNotice>
      );
    }
    return (
      <SubgroupFields>
        {columns.map((column) => (
          <WellCardDataValue
            key={column.property}
            propertyName={column.property}
            entityKind={entityKind}
            wellDetails={wellDetails}
            pending={status === "fetching"}
          />
        ))}
      </SubgroupFields>
    );
  };

  return (
    <SubgroupSection>
      <SubgroupToggle
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          // Space scrolls the tab body otherwise.
          event.preventDefault();
          toggle();
        }}>
        <IconComponent
          path={expanded ? mdiChevronDown : mdiChevronRight}
          size={CHEVRON_SIZE}
        />
        {title}
        {!unready && <SubgroupCount>{`(${columns.length})`}</SubgroupCount>}
      </SubgroupToggle>
      {expanded && body()}
    </SubgroupSection>
  );
}

interface WellCardDataValueInputModel {
  propertyName: string;
  primary?: boolean;
  copyValue?: boolean;
  alignRight?: boolean;
  entityKind?: EntityKind;
  wellDetails?: WellDetailCardData;
  /** This row's group is still arriving, so an absent value is not yet "n/a". */
  pending?: boolean;
  /**
   * Shown instead of "n/a" or the pending mark when the response carries no
   * value. For a cell that already has something true to say without the
   * fetch — the name, which is the uwi until the service names it.
   */
  fallback?: string;
}

export function WellCardDataValue({
  propertyName = "",
  primary = false,
  copyValue = true,
  alignRight = false,
  entityKind = EntityKind.Facility,
  wellDetails,
  pending = false,
  fallback
}: WellCardDataValueInputModel) {
  // custom hooks
  const property = useProperty(propertyName, false, "", entityKind);

  const wellData = wellDetails ?? {};
  const isDateGroup = property?.group === "Dates";
  const title =
    property?.group === "Performance"
      ? property?.lightContextTitle
      : property?.fullContextTitle;
  const alignLeft =
    entityKind === EntityKind.Facility &&
    (property?.group === "Header" || property?.group === "Dates");

  // A field the response has no value for is a row reading "n/a", not a row
  // that disappears: a custom field the service cannot resolve is the normal
  // case, and dropping the row left Org/Project/My Fields showing subgroup
  // headings with nothing under them.
  const rawValue = wellData[propertyName];
  // A cell with a fallback never reads "n/a" or waits: the response's own
  // "n/a" is an absence for it too, so the fallback covers that as well as a
  // key that has not arrived.
  const value = fallback
    ? isCopyableValue(rawValue)
      ? rawValue
      : fallback
    : propertyName in wellData
    ? rawValue
    : pending
    ? PENDING_VALUE
    : NOT_AVAILABLE;

  // Offered for what the cell shows, so a name standing in as the uwi can be
  // copied too — the identifier is the one most worth having. The pending mark
  // is not a value, whatever it looks like.
  const canCopy = value !== PENDING_VALUE && isCopyableValue(value);

  const copyToClipboard = useCallback(() => {
    if (!property || !canCopy) return;

    navigator.clipboard.writeText(value as string);
    toast.success(`${property.fullContextTitle} copied to clipboard`);
  }, [property, canCopy, value]);

  if (!property) return null;

  if (primary) {
    return (
      <WellDetailPrimaryCell>
        {value}
        {canCopy && (
          <WellDetailValueCopyButton onClick={copyToClipboard}>
            <IconComponent path={mdiContentCopy} size={1} />
          </WellDetailValueCopyButton>
        )}
      </WellDetailPrimaryCell>
    );
  }

  return (
    <WellDetailCell primary={primary}>
      <WellDetailTitle isDateGroup={isDateGroup} primary={primary}>
        {title}
      </WellDetailTitle>
      <WellDetailValue
        primary={primary}
        isDateGroup={isDateGroup}
        isNumber={property.dataType === "Integer" || property.dataType === "Number"}
        alignRight={alignRight}
        alignLeft={alignLeft}>
        {value}
        {copyValue && canCopy && (
          <WellDetailValueCopyButton onClick={copyToClipboard}>
            <IconComponent path={mdiContentCopy} size={1} />
          </WellDetailValueCopyButton>
        )}
      </WellDetailValue>
    </WellDetailCell>
  );
}

const WellCardContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
const OverlayContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(204, 204, 204, 0.8);
  z-index: 1;
`;
const OverlayMessages = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  z-index: inherit;
`;
const HeaderDetailsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  column-gap: 18px;
  padding: 10px;
  border-bottom: 1px solid #ccc;
`;
const WellDetailsGroupTitle = styled.div`
  padding-top: 10px;
  padding-left: 10px;
  color: var(--color-text);
  font-weight: 800;
`;
const WellDetailsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  column-gap: 18px;
  padding: 0 10px;
  border-bottom: 1px solid #ccc;
`;
/**
 * Holds the divider a subgroup's field container draws in every other tab.
 * It has to sit here rather than on the fields: those are unmounted while the
 * job is closed, and a collapsed geo tab must still read as the same divided
 * list of subgroups the ticket shows everywhere else.
 */
const SubgroupSection = styled.div`
  border-bottom: 1px solid #ccc;
`;
/**
 * The same heading as every other subgroup, made operable: it carries the
 * chevron and takes the click. Styled as a row rather than a button so the
 * geo tabs read like the rest of the ticket.
 */
const SubgroupToggle = styled(WellDetailsGroupTitle)`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  user-select: none;

  &:hover {
    color: var(--color-primary);
  }
`;
const SubgroupCount = styled.span`
  color: var(--color-action-button);
  font-weight: 400;
`;
/**
 * `content-visibility` earns its place here and nowhere else in the card: one
 * open subgroup can still be hundreds of rows, and with several open the
 * browser skips laying out the ones scrolled away. The intrinsic size is only
 * the placeholder height used before a section has been rendered once — `auto`
 * remembers the real one after that.
 */
const SubgroupFields = styled(WellDetailsContainer)`
  /* The section around it draws the divider, so this must not draw a second. */
  border-bottom: none;
  content-visibility: auto;
  contain-intrinsic-size: auto 600px;
`;
/**
 * What an open subgroup says instead of its rows — loading, or a failure with
 * a Retry. Sits where the rows will, so opening one does not jump the page.
 */
const SubgroupNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  color: var(--color-text);
`;

const PerformanceDetailsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  column-gap: 18px;
  padding: 0 10px;
  border-bottom: ${(props) =>
    props.className && props.className.includes("last-item") ? "1px solid #ccc" : "none"};
`;

const WellDetailCell = styled.div`
  display: flex;
`;
const WellDetailTitle = styled.div`
  width: ${(props) => (props.isDateGroup ? "65%" : "50%")};
  color: var(--color-action-button);
`;
const WellDetailValueCopyButton = styled.span`
  cursor: pointer;
  color: var(--color-action-button);
  opacity: 0;

  &:hover {
    color: var(--color-primary);
  }
`;
const WellDetailPrimaryCell = styled.div`
  display: flex;
  gap: 8px;
  grid-column: 1 / -1;
  color: var(--color-primary);
  font-size: 18px;
  font-weight: 800;

  &:hover > ${WellDetailValueCopyButton} {
    opacity: 1;
  }
`;
const WellDetailValue = styled.div`
  font-family: var(--fontMono);
  width: ${(props) => (props.isDateGroup ? "35%" : "50%")};
  display: flex;
  align-items: center;
  justify-content: ${(props) =>
    props.alignRight
      ? "right"
      : props.alignLeft
      ? "left"
      : props.isNumber
      ? "right"
      : "left"};
  gap: 8px;
  color: ${(props) => (props.primary ? "var(--color-primary)" : "var(--color-text)")};
  font-weight: ${(props) => (props.primary ? "800" : "auto")};

  &:hover > ${WellDetailValueCopyButton} {
    opacity: 1;
  }
`;

const TabbedInfoContainer = styled.div`
  height: 100%;
  overflow: hidden;

  .ant-tabs {
    height: 100%;
    overflow: hidden;

    .ant-tabs-content {
      height: 100%;
      overflow: hidden;

      .ant-tabs-tabpane {
        height: 100%;
        overflow: hidden;
      }
    }
  }
`;
const TabContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: auto;
`;
const TabEmptyMessage = styled.div`
  padding: 10px;
  color: var(--color-text);
`;
const TabErrorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  color: var(--color-text);
`;
const TabRetryButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  color: var(--color-action-button);
  text-decoration: underline;

  &:hover {
    color: var(--color-primary);
  }
`;
const TabLoadingContentContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow: auto;
`;
const ErrorContainer = styled.div`
  text-align: center;
  color: var(--color-white);
  background-color: var(--color-danger);
  border-radius: 5px;
  padding: 2px 20px;
  margin: 0 20px;
`;
const StyledSpinner = styled(IconSpinner)`
  margin: auto auto;
`;
const NoSelectedWellContainer = styled.div`
  color: var(--color-white);
  background-color: var(--color-text);
  border-radius: 5px;
  padding: 2px 20px;
  margin: 0 20px;
`;
